import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  removeFilterById,
  removeLocalFilter,
  selectNewFilter,
  selectShowAllEditOption,
  updateFilter,
  updateLocalFilter,
  selectLocalFilter,
} from '../../../../../../data/search';
import newsearchfilter from '../../SearchFilterNew.scss';
import {
  IncludeExcludeOptions,
  MlsListingStatus,
  MlsListingTypeOptions,
  OnMarketOption,
  fieldTagsMap,
  getSelectedMlsListingStatusIds,
} from '../../shared/ConstantsNew';
import ToggleList from '../../shared/ToggleSection/ToggleList';
import { useFilter } from '../../TagFilterContext';
import {
  generateMinMaxDateValue,
  generateMinMaxValue,
  getMinMaxDateToUpdate,
  getMinMaxValuesToUpdate,
} from '../../../../../../utils/filter_menu/minandmax';

const HeaderMlsFilter = ({ onSelect }) => {
  const dispatch = useDispatch();
  const filter = useSelector(selectNewFilter);
  const getLocalFilter = useSelector(selectLocalFilter);
  const { selectedTags = [], filteredFields = {}, navIndex = 0 } = useFilter() || {};
  const [isFilterApplied, setFilterApplied] = useState(false);
  const refs = useRef({});
  const [mls, setMLSValues] = useState({
    forSale: null,
    rental: null,
    mlsListingStatus: null,
    mlsListingDate: null,
    daysOnMarketMin: null,
    daysOnMarketMax: null,
    mlsListingAmountMin: null,
    mlsListingAmountMax: null,
    belowMarketPrice: null,
    mlsListingKeyword: null,
    mlsListingDateMin: null,
    mlsListingDateMax: null,
  });

  useEffect(() => {
    if (filter && JSON.stringify(filter) !== JSON.stringify(mls)) {
      setMLSValues(filter);
      setFilterApplied(false);
      setTimeout(() => setFilterApplied(true), 0);
    }
  }, [filter]);

  useEffect(() => {
    if (isFilterApplied) {
      applyFiltersBasedOnMls();
    }
  }, [isFilterApplied, mls]);

  const getBooleanOption = (value, options) => {
    return options.find((option) => option.value === value) || null;
  };

  const prevMlsValues = useRef(null);

  const resetfilter = () => {
    console.log('reset filter called');
    onSelect('all', false);
  };

  const applyFiltersBasedOnMls = () => {
    if (prevMlsValues.current && JSON.stringify(prevMlsValues.current) === JSON.stringify(mls)) {
      return;
    }

    prevMlsValues.current = mls;

    const fieldsToCheck = [
      {
        label: 'On Market',
        value: mls.forSale,
        key: 'forSale',
        isBoolean: true,
        options: OnMarketOption,
      },
      {
        label: 'Listing Type',
        value: mls.rental,
        key: 'rental',
        isBoolean: true,
        options: MlsListingTypeOptions,
      },
      {
        label: 'Listed Below Market Price',
        value: mls.belowMarketPrice,
        key: 'belowMarketPrice',
        isBoolean: true,
        options: IncludeExcludeOptions,
      },
      {
        label: 'MLS Status',
        value: mls.mlsListingStatus,
        key: 'mlsListingStatus',
        options: MlsListingStatus,
      },
      {
        label: 'MLS Keyword(s)',
        value: mls.mlsListingKeyword,
        key: 'mlsListingKeyword',
        isText: true,
      },
      // Date fields
      {
        label: 'MLS Status Date',
        min: mls.mlsListingDateMin,
        max: mls.mlsListingDateMax,
        key: 'mlsListingDate',
        isDate: true,
      },
      {
        label: 'Days On Market',
        min: mls.daysOnMarketMin,
        max: mls.daysOnMarketMax,
        key: 'daysOnMarket',
      },
      {
        label: 'MLS Listing Amount',
        min: mls.mlsListingAmountMin,
        max: mls.mlsListingAmountMax,
        key: 'mlsListingAmount',
      },
    ];

    fieldsToCheck.forEach(({ label, min, max, key, isBoolean, isText, isDate, value, options }) => {
      if (isBoolean) {
        if (value !== null && value !== '' && value !== undefined) {
          const option = getBooleanOption(value, options);
          if (option) {
            handleBooleanSelectPCNew(option, key, label);
          } else {
            //handleOldRemoveFilter(key);
            handleClearFilter(label);
          }
        }
      } else if (Array.isArray(options) && options.length) {
        // ATAI-217: `value` is normally the comma-joined id string this same
        // component writes via handleSelectAllClick's `.join(',')` — but
        // when AI Search resolves a multi-status prompt (e.g. "Sold and
        // Removed"), the filter value synced in from redux arrives as an
        // actual array instead, and `.split` isn't defined on arrays. Accept
        // either shape.
        // PROPS-3770: legacy vendor ids (EXPIRED, ...) select "Failed" rather than being dropped.
        const selectedValues = key === 'mlsListingStatus'
          ? getSelectedMlsListingStatusIds(value)
          : Array.isArray(value) ? value : value ? value.split(',') : [];
        const selectedOptions = options.filter((option) => selectedValues.includes(option.id));

        if (selectedOptions.length) {
          handleSelectAllClick(selectedOptions, label, key);
        } else {
          handleClearFilter(label);
        }
      } else if (isText) {
        if (value !== null && value !== '' && value !== undefined) {
          if (prevMlsValues.current?.[key] !== value) {
            inputValueChange(value, label, key);
          }
        }
      } else if (isDate) {
        if (
          (min !== null && min !== '' && min !== undefined) ||
          (max !== null && max !== '' && max !== undefined)
        ) {
          if (prevMlsValues.current?.[key] !== min || prevMlsValues.current?.[key] !== max) {
            comboDateValueChange(min, max, label, key);
          }
        }
      } else {
        if (
          (min !== null && min !== '' && min !== undefined) ||
          (max !== null && max !== '' && max !== undefined)
        ) {
          if (prevMlsValues.current?.[key] !== min || prevMlsValues.current?.[key] !== max) {
            comboBoxValueChange(min, max, label, key);
          }
        }
      }
    });
  };

  const getEditOptions = useSelector(selectShowAllEditOption);
  useEffect(() => {
    if (getEditOptions) {
      if (getEditOptions?.key === 'mls') {
        let key = getEditOptions?.apiKey.replace(/Max|Min/g, '');
        editSectionClick(key ? key : '');
      }
    }
  }, [getEditOptions]);

  const editSectionClick = (id) => {
    setTimeout(() => {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200); // 5000 milliseconds = 5 seconds
  };

  const handleSelectAllClick = (selectedOptions, leftValue, key) => {
    let name = '';
    if (selectedOptions?.length) {
      if (selectedOptions?.length > 1) {
        name = `${selectedOptions[0]?.name}  ( ${selectedOptions.length} )`;
      } else {
        name = `${selectedOptions[0]?.name}`;
      }

      const selectedIds = selectedOptions.map((element) => element.id).join(',');
      const fullSeletedNamesList = selectedOptions.map((element) => element.name).join(',');
      updateFilterOldStore(key, selectedIds);
      // meed to work morning
      updateFilterStore({ value: key, label: name }, key, leftValue, fullSeletedNamesList);
    } else {
      handleOldRemoveFilter(key);
      handleClearFilter(leftValue);
    }
  };

  const inputValueChange = (value, lable, key) => {
    updateFilterOldStore(key, value);
    updateFilterStoreCombox(value, key, lable);
  };

  // combox
  const comboBoxValueChange = (minValue, maxValue, leftLabel, key) => {
    const name = generateMinMaxValue(minValue, maxValue);
    const valuesToUpdate = getMinMaxValuesToUpdate(minValue, maxValue, key);
    valuesToUpdate.forEach(({ key, value }) => {
      updateFilterOldStore(key, value);
    });

    updateFilterStoreCombox(name, key, leftLabel);
  };

  // combo date
  const comboDateValueChange = (minValue, maxValue, leftLabel, key) => {
    const name = generateMinMaxDateValue(minValue, maxValue);
    const valuesToUpdate = getMinMaxDateToUpdate(minValue, maxValue, key);

    valuesToUpdate.forEach(({ key, value }) => {
      updateFilterOldStore(key, value);
    });

    updateFilterStoreCombox(name, key, leftLabel);
  };

  const handleBooleanSelectPCNew = (option, key, leftLabel) => {
    if (option.value === true || option.value === false) {
      updateFilterOldStore(key, option.value);
      updateFilterStore(option, key, leftLabel);
    } else {
      handleOldRemoveFilter(key);
      handleClearFilter(leftLabel);
    }
  };

  const updateFilterOldStore = (key, id) => {
    dispatch(updateFilter({ [key]: id }));
  };

  const updateFilterStore = (option, key, leftLabel, fullSeletedNamesList) => {
    dispatch(
      updateLocalFilter([
        {
          key: 'mls',
          id: leftLabel,
          name: option.label,
          apiKey: key,
          [key]: option.value,
          removeID: key,
          showALLDisplayValue: fullSeletedNamesList,
        },
      ])
    );
    // }
  };

  const updateFilterStoreCombox = (name, key, leftLabel) => {
    dispatch(
      updateLocalFilter([
        {
          key: 'mls',
          id: leftLabel,
          name: name,
          apiKey: key,
          removeID: key,
          showALLDisplayValue: name,
        },
      ])
    );
  };

  const handleClearFilter = (id) => {
    dispatch(removeLocalFilter(id));
  };

  const handleOldRemoveFilter = (id) => {
    dispatch(removeFilterById(id));
  };

  const getClassNames = (fieldLabel) => {
    const _tagsForField = getTagsForField(fieldLabel);
    const _mlsFilters = getLocalFilter.filter((filter) => filter.key === 'mls');
  };

  useEffect(() => {
    const highlightedField = Object.keys(filteredFields).find(
      (field) => filteredFields[field] === navIndex
    );
    if (highlightedField && refs.current[highlightedField]) {
      refs.current[highlightedField].focus({ preventScroll: true });
      refs.current[highlightedField].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [filteredFields, navIndex]);

  const getTagsForField = (fieldLabel) => {
    return fieldTagsMap[fieldLabel] || [];
  };

  const isFilteredSection = (fieldLabel) => {
    const tagsForField = getTagsForField(fieldLabel);
    return selectedTags.length > 0 && tagsForField.some((tag) => selectedTags.includes(tag));
  };

  return (
    <div
      className={classNames(
        getClassNames('On Market'),
        getClassNames('Listing Type'),
        getClassNames('MLS Status'),
        getClassNames('MLS Satus Date'),
        getClassNames('Days On Market'),
        getClassNames('MLS Listing Amount'),
        getClassNames('Listed Below Market Price'),
        getClassNames('MLS Keyword(s)')
      )}
    >
      <div
        className={classNames(
          newsearchfilter.pb_28.p_0,
          getClassNames('On Market'),
          getClassNames('Listing Type')
        )}
      >
        <ToggleList
          headerTitle={''}
          headerTitleShow={false}
          backGroudGray={false}
          selected={mls.forSale}
          options={OnMarketOption}
          isHighlightIndex={filteredFields['On Market'] === navIndex ? true : false}
          headerClassName={isFilteredSection('On Market') ? newsearchfilter.filteredSubTitle : ''}
          onSelectOption={(selectedOption) => {
            handleBooleanSelectPCNew(selectedOption, 'forSale', 'On Market');
            resetfilter();
          }}
        />
      </div>
    </div>
  );
};

export default HeaderMlsFilter;
